import type { HttpClient } from '@wix/cli-http-client';
import type { Options } from 'p-retry';
import pRetry from 'p-retry';
import { z } from 'zod';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getTestOverrides } from '@wix/cli-test-overrides';
import { getSdkSlotData } from '@wix/ambassador-auto-api-info-v1-sdk-slot-data/http';
import { listComponentByComponentType } from '@wix/ambassador-devcenter-components-search-v1-components-search/http';
import type { ComponentResult } from '@wix/ambassador-devcenter-components-search-v1-components-search/types';
import {
  ComponentType,
  ExtendingComponentType,
} from '@wix/ambassador-devcenter-components-search-v1-components-search/types';
import {
  sdkSlotDataSchema,
  backOfficeExtensionContainerSchema,
  backOfficePageSchema,
} from './schema.js';

export class ExtensionDataClient {
  private readonly retryOptions: Options;

  constructor(private readonly httpClient: HttpClient) {
    const { minRetryTimeout } = getTestOverrides();

    this.retryOptions = {
      retries: 3,
      minTimeout: minRetryTimeout,
      maxTimeout: 3 * 1000,
    };
  }

  private listComponentByComponentType = async ({
    componentTypes,
    hasReferencedComponents,
  }: {
    componentTypes: ComponentType[];
    hasReferencedComponents?: boolean;
  }) => {
    try {
      let hasNext = true;
      let allComponents: ComponentResult[] = [];
      let nextCursor: string | undefined | null;

      while (hasNext) {
        const { data } = await pRetry(
          () =>
            this.httpClient.request(
              listComponentByComponentType({
                componentTypes,
                hasReferencedComponents,
                cursor: nextCursor,
              })
            ),
          this.retryOptions
        );

        allComponents = allComponents.concat(data.components ?? []);
        nextCursor = data.pagingMetadata?.cursors?.next;
        hasNext = data.pagingMetadata?.hasNext ?? false;
      }

      // Temporary fix for appName containing html code of `&`
      // JIRA: https://wix.atlassian.net/browse/WOSDC-2020
      return allComponents.map((component) => ({
        ...component,
        appName: component.appName?.replaceAll('&amp;', '&'),
      }));
    } catch (error) {
      throw new CliError({
        code: CliErrorCode.FailedToListComponentByComponentType(),
        cause: error,
      });
    }
  };

  public getBackofficeExtensionContainers = async () => {
    const backofficeExtensionContainers =
      await this.listComponentByComponentType({
        componentTypes: [ComponentType.BACK_OFFICE_EXTENSION_CONTAINER],
      });

    try {
      return z
        .array(backOfficeExtensionContainerSchema)
        .parse(backofficeExtensionContainers);
    } catch (error) {
      throw new CliError({
        code: CliErrorCode.FailedToParseBackofficeExtensionContainer(),
        cause: error,
      });
    }
  };

  public getBackofficeExtensionWidgets = async () => {
    return this.getBackofficeExtensionContainers().then(
      (backofficeExtensionContainer) =>
        backofficeExtensionContainer.filter(
          (container) =>
            container.component.data.backOfficeExtensionContainer.extendable
              .extendedBy ===
            ExtendingComponentType.BACK_OFFICE_EXTENSION_WIDGET
        )
    );
  };

  public getBackofficeMenuItems = async () => {
    return this.getBackofficeExtensionContainers().then(
      (backofficeExtensionContainer) =>
        backofficeExtensionContainer.filter(
          (container) =>
            container.component.data.backOfficeExtensionContainer.extendable
              .extendedBy === ExtendingComponentType.BACK_OFFICE_MENU_ITEM
        )
    );
  };

  public getBackofficePages = async () => {
    const backofficePages = await this.listComponentByComponentType({
      componentTypes: [ComponentType.BACK_OFFICE_PAGE],
      hasReferencedComponents: true,
    });

    try {
      return z.array(backOfficePageSchema).parse(backofficePages);
    } catch (error) {
      throw new CliError({
        code: CliErrorCode.FailedToParseBackofficePages(),
        cause: error,
      });
    }
  };

  public getSdkSlotData = async ({
    appId,
    slotId,
  }: {
    appId: string;
    slotId: string;
  }) => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(getSdkSlotData({ slotId, appId })),
        this.retryOptions
      );

      return sdkSlotDataSchema.parse(data).sdkSlotData;
    } catch (error) {
      throw new CliError({
        code: CliErrorCode.FailedToGetSdkSlotData(),
        cause: error,
      });
    }
  };
}
